import {
  ArrowRight,
  BadgeAlert,
  Box,
  FileText,
  Loader2,
  PlusCircle,
  TrendingUp,
  AlertTriangle,
  Receipt,
  Activity,
} from 'lucide-react';
import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';

import { useAuth } from '@/context/AuthContext';
import { api } from '@/lib/api';

interface DailyStats {
  salesCountToday: number;
  salesAmountToday: number;
  totalStock: number;
  lowStockCount: number;
  recentSales: Array<{
    id: string;
    invoiceNumber: string;
    customerName: string;
    grandTotal: number;
    invoiceDate: string;
    status: string;
    paymentMode: string;
  }>;
  recentLowStock: Array<{
    id: string;
    name: string;
    sku: string;
    currentStock: number;
    lowStockThreshold: number;
  }>;
}

export function TenantDashboard() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [stats, setStats] = useState<DailyStats | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  const fetchDailyStats = async () => {
    if (!user?.tenantId) return;
    setLoading(true);
    setError('');
    try {
      const response = await api.get('/reports/daily-stats', {
        headers: { 'x-tenant-id': user.tenantId },
      });
      setStats(response.data.data);
    } catch (err: any) {
      console.error(err);
      setError('Unable to fetch live dashboard telemetry. Please check server connection.');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchDailyStats();
  }, [user?.tenantId]);

  if (loading) {
    return (
      <div className="flex flex-col items-center justify-center py-32 text-slate-400">
        <Loader2 className="w-8 h-8 animate-spin text-emerald-600 mb-2" />
        <span className="text-xs font-semibold">Consolidating dashboard metrics...</span>
      </div>
    );
  }

  if (error || !stats) {
    return (
      <div className="p-6 bg-rose-50 border border-rose-100 text-rose-700 font-semibold rounded-xl space-y-4 max-w-xl mx-auto mt-12 text-center">
        <AlertTriangle className="mx-auto w-12 h-12 text-rose-500" />
        <h2 className="text-lg font-bold">Dashboard Loading Error</h2>
        <p className="text-sm font-medium text-rose-600">{error || 'Something went wrong.'}</p>
        <button
          onClick={fetchDailyStats}
          className="btn-primary px-4 py-2 border border-rose-600 bg-rose-600 text-white text-xs font-bold rounded-lg hover:bg-rose-500 cursor-pointer shadow-sm transition-all"
        >
          Try Again
        </button>
      </div>
    );
  }

  // Formatting date helper
  const formatDate = (dateStr: string) => {
    const d = new Date(dateStr);
    return d.toLocaleDateString('en-IN', {
      day: 'numeric',
      month: 'short',
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  return (
    <div className="space-y-6 font-sans antialiased text-slate-800">
      {/* Dynamic Welcome Banner */}
      <div className="relative overflow-hidden rounded-2xl bg-gradient-to-r from-slate-900 via-slate-800 to-indigo-950 p-6 sm:p-8 text-white shadow-lg border border-slate-700/30">
        <div className="absolute right-0 top-0 -mr-16 -mt-16 w-64 h-64 bg-indigo-500/10 rounded-full blur-3xl pointer-events-none" />
        <div className="relative z-10 flex flex-col md:flex-row md:items-center md:justify-between gap-4">
          <div>
            <div className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full bg-indigo-500/20 border border-indigo-400/20 text-xs font-semibold text-indigo-200 mb-3">
              <span className="w-1.5 h-1.5 rounded-full bg-indigo-400 animate-ping" />
              Store Dashboard
            </div>
            <h1 className="text-2xl sm:text-3xl font-extrabold tracking-tight">
              Welcome back, {user?.firstName || 'User'}!
            </h1>
            <p className="mt-1.5 text-sm text-slate-350 max-w-2xl font-light">
              Here is your textile retail analytics overview for today. Monitor live sales, cashier billing operations, and alert thresholds.
            </p>
          </div>
          <div className="flex items-center gap-2 shrink-0">
            <button
              onClick={() => navigate('/billing?mode=terminal')}
              className="px-4 py-2.5 bg-emerald-600 hover:bg-emerald-500 active:bg-emerald-700 text-white rounded-lg text-xs font-bold transition-all shadow-md flex items-center gap-1.5 cursor-pointer border border-emerald-500 hover:scale-[1.02]"
            >
              <PlusCircle size={14} />
              <span>New POS Billing</span>
            </button>
            <button
              onClick={fetchDailyStats}
              className="px-4 py-2.5 bg-white/10 hover:bg-white/20 active:bg-white/30 text-white border border-white/10 rounded-lg text-xs font-semibold transition-all shadow-sm cursor-pointer"
            >
              Sync live stats
            </button>
          </div>
        </div>
      </div>

      {/* Main Metrics Panels */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-5">
        {/* Today's Sales amount */}
        <div className="card bg-white border border-slate-100 p-5 rounded-xl shadow-sm space-y-4 hover:shadow transition-all relative overflow-hidden group">
          <div className="flex justify-between items-start">
            <div className="space-y-1">
              <span className="text-[10px] text-slate-400 font-bold uppercase tracking-wider block">
                Today's Revenue
              </span>
              <p className="text-2xl font-extrabold text-slate-800 font-mono">
                ₹{stats.salesAmountToday.toLocaleString('en-IN')}
              </p>
            </div>
            <div className="p-2.5 bg-emerald-50 rounded-xl text-emerald-650 border border-emerald-100 group-hover:scale-110 transition-transform">
              <TrendingUp size={20} />
            </div>
          </div>
          <div className="flex items-center gap-1 text-[11px] text-emerald-600 font-semibold bg-emerald-50/50 p-2 rounded-lg border border-emerald-100/50 w-fit">
            <span className="w-1.5 h-1.5 rounded-full bg-emerald-500 animate-pulse" />
            <span>Updated live</span>
          </div>
        </div>

        {/* Today's Sales Count */}
        <div className="card bg-white border border-slate-100 p-5 rounded-xl shadow-sm space-y-4 hover:shadow transition-all relative overflow-hidden group">
          <div className="flex justify-between items-start">
            <div className="space-y-1">
              <span className="text-[10px] text-slate-400 font-bold uppercase tracking-wider block">
                Invoice Count (Today)
              </span>
              <p className="text-2xl font-extrabold text-slate-800 font-mono">
                {stats.salesCountToday} bills
              </p>
            </div>
            <div className="p-2.5 bg-indigo-50 rounded-xl text-indigo-650 border border-indigo-100 group-hover:scale-110 transition-transform">
              <Receipt size={20} />
            </div>
          </div>
          <span className="text-[10px] text-slate-400 font-mono font-medium block">
            Average: ₹
            {stats.salesCountToday > 0
              ? Math.round(stats.salesAmountToday / stats.salesCountToday).toLocaleString('en-IN')
              : 0}{' '}
            per ticket
          </span>
        </div>

        {/* Total Stock Volume */}
        <div className="card bg-white border border-slate-100 p-5 rounded-xl shadow-sm space-y-4 hover:shadow transition-all relative overflow-hidden group">
          <div className="flex justify-between items-start">
            <div className="space-y-1">
              <span className="text-[10px] text-slate-400 font-bold uppercase tracking-wider block">
                Total Inventory Stock
              </span>
              <p className="text-2xl font-extrabold text-slate-800 font-mono">
                {stats.totalStock.toLocaleString('en-IN')} units
              </p>
            </div>
            <div className="p-2.5 bg-sky-50 rounded-xl text-sky-650 border border-sky-100 group-hover:scale-110 transition-transform">
              <Box size={20} />
            </div>
          </div>
          <span className="text-[10px] text-slate-400 font-mono font-medium block">
            Across all active catalog categories
          </span>
        </div>

        {/* Low Stock count alert */}
        <div className="card bg-white border border-slate-100 p-5 rounded-xl shadow-sm space-y-4 hover:shadow transition-all relative overflow-hidden group">
          <div className="flex justify-between items-start">
            <div className="space-y-1">
              <span className="text-[10px] text-slate-400 font-bold uppercase tracking-wider block">
                Low Stock Warning SKUs
              </span>
              <p
                className={`text-2xl font-extrabold font-mono ${
                  stats.lowStockCount > 0 ? 'text-rose-600 animate-pulse' : 'text-slate-800'
                }`}
              >
                {stats.lowStockCount} items
              </p>
            </div>
            <div
              className={`p-2.5 rounded-xl border group-hover:scale-110 transition-transform ${
                stats.lowStockCount > 0
                  ? 'bg-rose-50 text-rose-600 border-rose-100'
                  : 'bg-slate-100 text-slate-500 border-slate-200'
              }`}
            >
              <BadgeAlert size={20} />
            </div>
          </div>
          <span className="text-[10px] text-slate-400 font-mono font-medium block">
            Requires restocking priority
          </span>
        </div>
      </div>

      {/* Main Grid: Left Recent Transactions, Right Low Stock Details */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Recent Invoices Table */}
        <div className="lg:col-span-2 card bg-white border border-slate-100 p-5 rounded-xl shadow-sm space-y-4">
          <div className="flex items-center justify-between border-b border-slate-100 pb-3">
            <h2 className="text-sm font-bold text-slate-800 uppercase tracking-wider flex items-center gap-1.5">
              <FileText size={16} className="text-indigo-650" />
              <span>Recent Transactions</span>
            </h2>
            <button
              onClick={() => navigate('/billing')}
              className="text-[11px] font-bold text-indigo-600 hover:text-indigo-700 flex items-center gap-1 group cursor-pointer transition-colors"
            >
              <span>View Logs</span>
              <ArrowRight size={12} className="group-hover:translate-x-0.5 transition-transform" />
            </button>
          </div>

          {stats.recentSales.length === 0 ? (
            <div className="text-center py-12 text-slate-400">
              <Receipt className="mx-auto mb-2.5 opacity-30 text-indigo-650" size={32} />
              <p className="text-xs font-semibold">No invoices generated yet today.</p>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-left text-xs">
                <thead>
                  <tr className="border-b border-slate-100 text-slate-400 font-bold uppercase tracking-wider bg-slate-50/50">
                    <th className="py-2.5 px-3 text-center w-12">S.No.</th>
                    <th className="py-2.5 px-3">Invoice No</th>
                    <th className="py-2.5 px-2">Customer</th>
                    <th className="py-2.5 px-2">Date</th>
                    <th className="py-2.5 px-2">Mode</th>
                    <th className="py-2.5 px-3 text-right">Amount</th>
                    <th className="py-2.5 px-3 text-center">Status</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-100">
                  {stats.recentSales.map((sale, idx) => (
                    <tr key={sale.id} className="hover:bg-slate-50/50 transition-colors">
                      <td className="py-3 px-3 text-center text-slate-500 font-medium">{idx + 1}</td>
                      <td className="py-3 px-3 font-bold text-slate-800 font-mono">
                        {sale.invoiceNumber}
                      </td>
                      <td className="py-3 px-2 text-slate-600 font-semibold">{sale.customerName}</td>
                      <td className="py-3 px-2 text-slate-450">{formatDate(sale.invoiceDate)}</td>
                      <td className="py-3 px-2">
                        <span className="px-1.5 py-0.5 rounded text-[10px] font-bold bg-slate-100 text-slate-650 border border-slate-200">
                          {sale.paymentMode}
                        </span>
                      </td>
                      <td className="py-3 px-3 text-right font-mono font-bold text-slate-800">
                        ₹{sale.grandTotal.toLocaleString('en-IN')}
                      </td>
                      <td className="py-3 px-3 text-center">
                        <span
                          className={`px-2 py-0.5 rounded-full text-[9px] font-extrabold border ${
                            sale.status === 'PAID'
                              ? 'bg-emerald-50 text-emerald-700 border-emerald-100'
                              : 'bg-amber-50 text-amber-700 border-amber-200'
                          }`}
                        >
                          {sale.status}
                        </span>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>

        {/* Low Stock Detail list */}
        <div className="card bg-white border border-slate-100 p-5 rounded-xl shadow-sm space-y-4">
          <div className="flex items-center justify-between border-b border-slate-100 pb-3">
            <h2 className="text-sm font-bold text-slate-800 uppercase tracking-wider flex items-center gap-1.5">
              <BadgeAlert size={16} className="text-rose-600" />
              <span>Low Stock Alerts</span>
            </h2>
            <button
              onClick={() => navigate('/inventory')}
              className="text-[11px] font-bold text-rose-600 hover:text-rose-700 flex items-center gap-1 group cursor-pointer transition-colors"
            >
              <span>Manage</span>
              <ArrowRight size={12} className="group-hover:translate-x-0.5 transition-transform" />
            </button>
          </div>

          {stats.recentLowStock.length === 0 ? (
            <div className="text-center py-12 text-slate-400">
              <Box className="mx-auto mb-2.5 opacity-30 text-emerald-650" size={32} />
              <p className="text-xs font-semibold text-emerald-600">All catalog stock healthy.</p>
            </div>
          ) : (
            <div className="space-y-3.5">
              {stats.recentLowStock.map((prod) => (
                <div
                  key={prod.id}
                  className="flex items-center justify-between p-3 rounded-lg border border-slate-100 bg-slate-50/50 hover:bg-slate-50 transition-colors"
                >
                  <div className="space-y-1">
                    <span className="text-[11px] font-bold text-slate-800 truncate block max-w-[150px]">
                      {prod.name}
                    </span>
                    <span className="text-[9px] font-mono text-slate-400 block">{prod.sku}</span>
                  </div>
                  <div className="text-right">
                    <span className="text-xs font-extrabold text-rose-600 font-mono block">
                      {prod.currentStock} units
                    </span>
                    <span className="text-[9px] text-slate-400 block">
                      Limit: {prod.lowStockThreshold}
                    </span>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>

      {/* Helpful Operational tips banner */}
      <div className="card bg-slate-50 border border-slate-150 p-4 rounded-xl flex items-start gap-3">
        <div className="p-1.5 bg-indigo-50 rounded-lg text-indigo-600 shrink-0">
          <Activity size={16} />
        </div>
        <div className="space-y-1">
          <span className="text-xs font-bold text-slate-700 block">Operational Guideline Summary</span>
          <p className="text-xs text-slate-500 leading-relaxed font-semibold">
            Remember to review the <strong className="text-slate-700">Reports</strong> tab regularly for GST returns (CGST/SGST/IGST slab logs) and stock valuations. Make sure all items have exact GST rates mapped so the invoices compile correctly for GSTR-1 audits.
          </p>
        </div>
      </div>
    </div>
  );
}
